/**
 * The event queue (plan.md §62.3).
 *
 * A festival is tens of thousands of people with no reception. An analytics
 * call that assumes the network drops exactly the events that matter most —
 * the saves made in front of a stage, at 2am, with no signal. So nothing is
 * ever sent at the moment it happens: events go into IndexedDB, and a flush
 * pushes them when there is something to push them over.
 *
 * Three rules this file exists to keep:
 *
 *  1. **Never awaited on a user path.** `track()` returns void, not a promise,
 *     and swallows its own failures. A visitor tapping "save" must never wait
 *     on a write to a stats queue, and must certainly never see it fail.
 *  2. **Idempotent on a client-generated id.** The id is assigned when the
 *     event is QUEUED, not when it is sent, so a retried batch is a no-op at
 *     the server rather than a doubled crowd count.
 *  3. **Per-edition device id, and nothing more.** §62.4: no IP, no user
 *     agent, no location — none of which this file has any way to collect.
 */
import { db } from './db';
import { SYNC_TIMEOUT_MS, withTimeout } from './net';
import * as apiModule from '@/lib/api';
import { apiUrl } from '@/lib/tenant';
import { readJson, writeJson } from '@/lib/storage';
import { MAX_EVENT_BATCH, type AppEvent, type EventType } from '@myrave/shared';
import { isFramed } from '@/lib/framed';

/** What a queued event looks like on the device. */
export interface QueuedEvent {
  /** The client-generated id — also the server's idempotency key. */
  id: string;
  editionId: string;
  event: AppEvent;
  queuedAt: number;
}

/**
 * A random id, from the platform's CSPRNG where there is one.
 *
 * Uniqueness is what stops a retry double-counting, so a weak fallback is a
 * correctness problem rather than a security one — hence the time prefix,
 * which keeps ids unique per device even if the random half collides.
 */
function newId(): string {
  const random = globalThis.crypto?.randomUUID?.()
    ?? Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
  return `${Date.now().toString(36)}-${random}`.replace(/[^A-Za-z0-9_-]/g, '').slice(0, 64);
}

/**
 * This device, for this edition, and deliberately no further.
 *
 * §62.6 rules out following one person from one festival to the next, and the
 * only way to keep that promise is for there to be no identifier that could.
 * A new edition gets a new id; the two are not linkable, here or on the server.
 */
export function deviceId(editionId: string): string {
  const existing = readJson<string | null>(editionId, 'device', null);
  if (typeof existing === 'string' && existing.length > 0) return existing;
  const fresh = newId();
  writeJson(editionId, 'device', fresh);
  return fresh;
}

type EventInput =
  | { type: 'attend' | 'map_open' | 'map_pin' | 'plan_share' | 'plan_open' }
  | { type: 'bundle_download'; version: string }
  | { type: 'act_save' | 'act_unsave' | 'act_view'; actSlug: string }
  | { type: 'search'; query: string; resultCount: number }
  | { type: 'ics_export'; actCount: number }
  | { type: 'session'; online: boolean; installed: boolean }
  | { type: 'ticket_click'; place: 'menu' | 'announced' | 'info' | 'tickets' }
  // A ticket saved on the phone (step 91) — that it happened, nothing of the ticket.
  | { type: 'ticket_saved' };

/**
 * Record that something happened. **Fire and forget, by design.**
 *
 * Returns void rather than a promise so that no call site can accidentally
 * await it, and so that adding a measurement to a component can never slow a
 * render or delay a navigation.
 */
export function track(editionId: string, input: EventInput): void {
  // The wizard's preview is an admin looking, not a raver: never counted.
  if (isFramed()) return;
  const event = { id: newId(), occurredAt: Date.now(), ...input } as AppEvent;
  void db.events
    .put({ id: event.id, editionId, event, queuedAt: Date.now() })
    // A device with IndexedDB blocked (private mode, a locked-down browser)
    // simply records nothing. Statistics are never worth a broken app.
    .catch(() => undefined);
}

export function queuedCount(editionId: string): Promise<number> {
  return db.events.where('editionId').equals(editionId).count();
}

export interface FlushResult {
  sent: number;
  accepted: number;
  duplicates: number;
}

/**
 * Push queued events for one edition, oldest first, and drop what the server
 * has taken.
 *
 * Rows are deleted only after a 2xx. A failed flush leaves the queue exactly
 * as it was, which is what makes "sync when you get signal, days later" work
 * rather than being a phrase in a document.
 *
 * A DUPLICATE IS A SUCCESS. The server reporting that it already had these
 * rows means the previous attempt landed and only the response was lost —
 * the single most common outcome on festival wifi — so those rows are cleared
 * too. Keeping them would mean a queue that can never drain.
 */
/**
 * The bearer header, when there is a session. The refresh cookie is
 * `Path=/auth` and never reaches this route, so without it every flush was
 * ANONYMOUS — refused outright wherever `REQUIRE_ACCOUNT` is on (pre-launch
 * production, staging and dev), and a signed-in person's events never
 * credited to them. Found 2026-09-29.
 */
function bearer(): Record<string, string> {
  try {
    return apiModule.authHeader?.() ?? {};
  } catch {
    return {};
  }
}

/**
 * Refusals that asking again cannot change: a malformed or oversized batch, an
 * edition that is gone. Such a batch is DROPPED — kept, it sits at the head of
 * the queue and nothing behind it is ever sent. Not 401 (signed out: kept for
 * the next session), 403 (an account gate that clears), 408 or 429.
 */
const DROP_ON = new Set([400, 404, 410, 413, 422]);

export async function flushEvents(editionId: string): Promise<FlushResult> {
  const queued = await db.events
    .where('editionId').equals(editionId)
    .sortBy('queuedAt')
    .then((rows) => rows.slice(0, MAX_EVENT_BATCH));

  if (queued.length === 0) return { sent: 0, accepted: 0, duplicates: 0 };

  const res = await withTimeout(fetch(`${apiUrl()}/editions/${editionId}/events`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json', ...bearer() },
    body: JSON.stringify({
      deviceId: deviceId(editionId),
      events: queued.map((row) => row.event),
    }),
  }), SYNC_TIMEOUT_MS);

  if (DROP_ON.has(res.status)) {
    await db.events.bulkDelete(queued.map((row) => row.id));
    throw new Error(`event flush refused for good (${res.status}); ${queued.length} event(s) dropped`);
  }
  if (!res.ok) throw new Error(`event flush failed (${res.status})`);

  const body = (await res.json().catch(() => ({}))) as { accepted?: number; duplicates?: number };
  await db.events.bulkDelete(queued.map((row) => row.id));

  return {
    sent: queued.length,
    accepted: body.accepted ?? 0,
    duplicates: body.duplicates ?? 0,
  };
}

/**
 * Flush without ever propagating a failure.
 *
 * What the app calls on reconnect and on the page going away. There is no
 * useful thing for a visitor to do about a failed stats flush, and the queue
 * survives to try again.
 */
export async function flushQuietly(editionId: string): Promise<void> {
  try {
    await flushEvents(editionId);
  } catch {
    /* the queue keeps the events; the next flush takes them */
  }
}

/** Is this running as an installed app rather than a browser tab? */
export function isInstalled(): boolean {
  try {
    return window.matchMedia?.('(display-mode: standalone)').matches === true;
  } catch {
    return false;
  }
}

/**
 * Flush on reconnect and when the page is going away.
 *
 * `visibilitychange` to hidden is the last reliable moment on mobile — a tab
 * that is backgrounded may never run another line, and `beforeunload` does not
 * fire on iOS. Returns a teardown.
 */
export function startEventSync(editionId: string): () => void {
  const flush = () => void flushQuietly(editionId);

  const onVisibility = () => {
    if (document.visibilityState === 'hidden') flush();
  };

  window.addEventListener('online', flush);
  document.addEventListener('visibilitychange', onVisibility);
  flush();

  return () => {
    window.removeEventListener('online', flush);
    document.removeEventListener('visibilitychange', onVisibility);
  };
}

/** Type-safe helper for the one place a raw type string is convenient. */
export type { EventType };
